import { useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  canEditLinkedAccountOwnerAndSyncSelector,
  hasAccessToEditProfileSelector,
} from '../../../../../../../CommonSelectors/CommonAreaSelector';
import { canEditAggregatedLinkedAccountDescriptionOnly as getCanEditAggregatedLinkedAccountDescriptionOnly } from '../../../accountEditAccess';
import { AccountFormContext } from '../context/AccountFormProvider';

interface AccountFormFieldDisabledOptions {
  skipAccountFormProvider?: boolean;
  accountDescriptionField?: boolean;
}

export const useCanEditLinkedAccountOwnerAndSync = ({
  skipAccountFormProvider = false,
}: AccountFormFieldDisabledOptions = {}) => {
  const accountFormContext = useContext(AccountFormContext);
  const canEditLinkedAccountOwnerAndSync = useSelector(canEditLinkedAccountOwnerAndSyncSelector);
  const hasEditProfileAccess = useSelector(hasAccessToEditProfileSelector);

  if (skipAccountFormProvider) {
    return {
      canEditOwnerAndSyncOnly: false,
      canEditAggregatedLinkedAccountDescriptionOnly: false,
      canEditLinkedAccountLimited: false,
    };
  }

  if (!accountFormContext) {
    throw new Error('useCanEditLinkedAccountOwnerAndSync must be used within a AccountFormProvider');
  }

  const { accounts, localSaveOptions, accountId } = accountFormContext;

  const selectedAccount = localSaveOptions
    ? localSaveOptions.data?.bankAccount
    : accounts.find(({ AccountId }) => AccountId === accountId);

  const isLinkedAccount = !!selectedAccount?.IsLinked;
  const canEditOwnerAndSyncOnly =
    !localSaveOptions && isLinkedAccount && canEditLinkedAccountOwnerAndSync;
  const canEditAggregatedLinkedAccountDescriptionOnly =
    getCanEditAggregatedLinkedAccountDescriptionOnly(selectedAccount, hasEditProfileAccess);

  return {
    canEditOwnerAndSyncOnly,
    canEditAggregatedLinkedAccountDescriptionOnly,
    canEditLinkedAccountLimited:
      canEditOwnerAndSyncOnly || canEditAggregatedLinkedAccountDescriptionOnly,
  };
};

/** When the form is globally disabled, owner/sync and description fields may remain editable in limited linked-account mode. */
export const useAccountFormFieldDisabled = (
  ownerAndSyncField = false,
  {
    skipAccountFormProvider = false,
    accountDescriptionField = false,
  }: AccountFormFieldDisabledOptions = {},
) => {
  const {
    formState: { disabled },
  } = useFormContext();
  const {
    canEditOwnerAndSyncOnly,
    canEditAggregatedLinkedAccountDescriptionOnly,
  } = useCanEditLinkedAccountOwnerAndSync({
    skipAccountFormProvider,
  });

  if (ownerAndSyncField && canEditOwnerAndSyncOnly) {
    return false;
  }

  if (accountDescriptionField && canEditAggregatedLinkedAccountDescriptionOnly) {
    return false;
  }

  return disabled;
};
